"use client";

import { useEffect } from "react";

/**
 * error.tsx only catches errors thrown *inside* the root layout's children —
 * a throw in layout.tsx itself (fonts, providers, nav) skips it entirely and
 * falls through to Next's bare default screen with zero visibility. This is
 * the only boundary above that. It replaces <html>/<body> wholesale, so no
 * import from layout.tsx (globals.css, fonts) is guaranteed to have rendered
 * — inline styles only, no Tailwind classes.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    fetch("/api/client-error", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        message: error.message,
        digest: error.digest,
        stack: error.stack,
        path: typeof window !== "undefined" ? window.location.pathname : null,
        boundary: "global",
      }),
      keepalive: true,
    }).catch(() => {});
  }, [error]);

  return (
    <html lang="en">
      <body
        style={{
          fontFamily:
            "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          display: "flex",
          minHeight: "100vh",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          padding: "24px",
        }}
      >
        <p style={{ fontSize: 12, letterSpacing: "0.05em", color: "#a3a3a3", textTransform: "uppercase" }}>
          Finseats
        </p>
        <h1 style={{ marginTop: 12, fontSize: 24, fontWeight: 600, color: "#171717" }}>
          Something went wrong
        </h1>
        <p style={{ marginTop: 8, maxWidth: 420, fontSize: 15, color: "#525252" }}>
          This page hit an unexpected error. Reloading usually fixes it.
        </p>
        <div style={{ marginTop: 24, display: "flex", gap: 12 }}>
          <button
            type="button"
            onClick={() => reset()}
            style={{
              height: 44,
              padding: "0 20px",
              borderRadius: 6,
              border: "none",
              background: "#172f9e",
              color: "#fff",
              fontWeight: 600,
              fontSize: 14,
              cursor: "pointer",
            }}
          >
            Try again
          </button>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              height: 44,
              padding: "0 20px",
              borderRadius: 6,
              border: "1px solid #e5e5e5",
              background: "#fff",
              color: "#404040",
              fontWeight: 600,
              fontSize: 14,
              cursor: "pointer",
            }}
          >
            Reload page
          </button>
        </div>
      </body>
    </html>
  );
}
